import { useQuery } from '@tanstack/react-query';
import { fetchData } from '../services/api';
import type { Child, Communication, Notification } from '@shared/types';

export default function Dashboard() {
  const { data: children, isLoading: loadingChildren } = useQuery({
    queryKey: ['children'],
    queryFn: () => fetchData<Child[]>('/children'),
  });

  const { data: urgentComms, isLoading: loadingComms } = useQuery({
    queryKey: ['communications', 'urgent'],
    queryFn: () => fetchData<Communication[]>('/communications?priority=urgent&limit=5'),
  });

  const { data: notifications, isLoading: loadingNotifications } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => fetchData<Notification[]>('/notifications?read=false&limit=5'),
  });

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Dashboard</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <TimeDisplay />
      </header>

      {/* Children Priority Section */}
      <section className="card child-related">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <span className="text-warmth-500">❤️</span>
            Children
          </h2>
          <span className="badge-warmth">Priority</span>
        </div>

        {loadingChildren ? (
          <div className="space-y-3">
            <div className="skeleton h-16 w-full rounded-soft" />
            <div className="skeleton h-16 w-full rounded-soft" />
          </div>
        ) : children && children.length > 0 ? (
          <div className="space-y-3">
            {children.map((child) => (
              <ChildCard key={child.id} child={child} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="👶"
            title="No children added yet"
            description="Add your children to track custody schedules, school events, and more."
            actionLabel="Add Child"
            actionHref="/children"
          />
        )}
      </section>

      {/* Urgent Communications */}
      <section className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Needs Attention</h2>
          {urgentComms && urgentComms.length > 0 && (
            <span className="badge-urgent">{urgentComms.length}</span>
          )}
        </div>

        {loadingComms ? (
          <div className="space-y-3">
            <div className="skeleton h-20 w-full rounded-soft" />
            <div className="skeleton h-20 w-full rounded-soft" />
          </div>
        ) : urgentComms && urgentComms.length > 0 ? (
          <div className="space-y-3">
            {urgentComms.map((comm) => (
              <CommunicationCard key={comm.id} communication={comm} />
            ))}
          </div>
        ) : (
          <p className="text-center py-6 text-neutral-500 dark:text-neutral-400">
            Nothing urgent right now. Nice!
          </p>
        )}
      </section>

      {/* Recent Notifications */}
      <section className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent Alerts</h2>
          {notifications && notifications.length > 0 && (
            <span className="badge-primary">{notifications.length}</span>
          )}
        </div>

        {loadingNotifications ? (
          <div className="space-y-2">
            <div className="skeleton h-12 w-full rounded-soft" />
            <div className="skeleton h-12 w-full rounded-soft" />
          </div>
        ) : notifications && notifications.length > 0 ? (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
          </div>
        ) : (
          <p className="text-center py-4 text-neutral-500 dark:text-neutral-400">
            All caught up!
          </p>
        )}
      </section>

      {/* Quick Actions */}
      <section className="grid grid-cols-2 gap-3">
        <QuickAction
          icon="✏️"
          label="Draft Message"
          href="/inbox?action=compose"
          color="primary"
        />
        <QuickAction
          icon="📅"
          label="Add Event"
          href="/children?action=event"
          color="warmth"
        />
        <QuickAction
          icon="💵"
          label="Log Expense"
          href="/finances?action=expense"
          color="calm"
        />
        <QuickAction
          icon="🤖"
          label="Ask Claude"
          href="/inbox?action=ai"
          color="neutral"
        />
      </section>
    </div>
  );
}

function TimeDisplay() {
  return (
    <div className="text-right">
      <div className="time-display">
        {new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })}
      </div>
    </div>
  );
}

function ChildCard({ child }: { child: Child }) {
  const today = new Date();
  const isWithMe = child.custodySchedule ? checkCustody(child.custodySchedule, today) : null;

  return (
    <div className="flex items-center gap-4 p-3 rounded-soft bg-warmth-50 dark:bg-warmth-900/20">
      <div className="w-12 h-12 rounded-full bg-warmth-200 dark:bg-warmth-800 flex items-center justify-center text-xl">
        {child.name.charAt(0)}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-neutral-900 dark:text-neutral-100">{child.name}</h3>
        {isWithMe !== null && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {isWithMe ? '🏠 With you today' : '📍 With co-parent'}
          </p>
        )}
      </div>
      <button className="btn-ghost p-2">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

function checkCustody(_schedule: Child['custodySchedule'], _date: Date): boolean {
  // Placeholder - would implement actual custody logic
  return true;
}

function CommunicationCard({ communication }: { communication: Communication }) {
  const priorityClasses = {
    urgent: 'priority-urgent',
    high: 'priority-high',
    normal: 'priority-normal',
    low: 'priority-low',
  };

  return (
    <div className={`p-4 rounded-soft ${priorityClasses[communication.priority]} ${communication.relatedChildId ? 'child-related' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium uppercase tracking-wide opacity-70">
              {communication.source}
            </span>
            {communication.relatedChildId && (
              <span className="text-xs">👶</span>
            )}
          </div>
          <h3 className="font-medium truncate">{communication.subject}</h3>
          <p className="text-sm opacity-80 truncate">{communication.from.name || communication.from.email}</p>
        </div>
        <span className="text-xs whitespace-nowrap opacity-70">
          {formatRelativeTime(communication.receivedAt)}
        </span>
      </div>
    </div>
  );
}

function NotificationItem({ notification }: { notification: Notification }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-soft hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
      <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-sm">
        {getNotificationIcon(notification.type)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{notification.title}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{notification.body}</p>
      </div>
    </div>
  );
}

function getNotificationIcon(type: Notification['type']): string {
  const icons: Record<Notification['type'], string> = {
    child_event: '👶',
    custody_reminder: '📅',
    communication: '📧',
    finance_alert: '💰',
    deadline: '⏰',
    webhook: '🔔',
    system: '⚙️',
  };
  return icons[type] || '📌';
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  return `${days}d`;
}

function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  actionHref,
}: {
  icon: string;
  title: string;
  description: string;
  actionLabel: string;
  actionHref: string;
}) {
  return (
    <div className="text-center py-8">
      <span className="text-4xl mb-4 block">{icon}</span>
      <h3 className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">{title}</h3>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4 readable-text">{description}</p>
      <a href={actionHref} className="btn-warmth text-sm">
        {actionLabel}
      </a>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  href,
  color,
}: {
  icon: string;
  label: string;
  href: string;
  color: 'primary' | 'warmth' | 'calm' | 'neutral';
}) {
  const colorClasses = {
    primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    warmth: 'bg-warmth-100 dark:bg-warmth-900/30 text-warmth-700 dark:text-warmth-300',
    calm: 'bg-calm-100 dark:bg-calm-900/30 text-calm-700 dark:text-calm-300',
    neutral: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300',
  };

  return (
    <a
      href={href}
      className={`card-interactive flex flex-col items-center justify-center py-6 ${colorClasses[color]}`}
    >
      <span className="text-2xl mb-2">{icon}</span>
      <span className="text-sm font-medium">{label}</span>
    </a>
  );
}
